import { toastr } from "react-redux-toastr";
import React, { useRef, useEffect } from "react";
import { FormikWizard } from "formik-wizard-form";
import { useHistory, useParams } from "react-router-dom";
import { Stepper, Step, StepLabel, Box } from "@mui/material";
// components
import MyButton from "components/button/Button";
import FinanceService from "Lib/API/Finance/index";
import PreSchoolService from "Lib/API/PreSchool/index";
import { useApiAction, useFetchData } from "hook/useFetchData";
// direct components
import "./member.scss";
import Pricing from "./view/pricing";
import CreateForm from "./view/create-form";
import { informationSchema, omitKeys } from "./validation";
import FormInformation from "./view/form-information";
import { mapPricingItem, submitButton } from "./helper";
import { cancelButton, getInitialValues } from "./helper";
import { dynamicPricingSchema, finalFormSchema } from "./validation";
import { membershipPricingSchema, touchStepFields } from "./validation";

const steps = ["Form sections", "Membership Pricing", "Add-Ons"];

const CreateMember = () => {
  const formikRef = useRef();
  const { push } = useHistory();
  const { id: formId } = useParams();
  // custom hook
  const {
    data: ledgers,
    loading,
    fetchWithDelay,
  } = useFetchData(FinanceService.getLedgerSubLedgerList);

  const {
    data: memberById,
    loading: memberLoading,
    fetchWithDelay: fetchMemberWithDelay,
  } = useFetchData(PreSchoolService.getMemberById);

  const { execute: createMember } = useApiAction((token, body) =>
    PreSchoolService.createMemberForm(body, token)
  );
  const { execute: updateMember } = useApiAction((token, body, formId) =>
    PreSchoolService.updateMember(token, body, formId)
  );

  useEffect(() => {
    return formId && fetchMemberWithDelay(formId);
  }, [formId]);

  useEffect(() => {
    return fetchWithDelay();
  }, []);

  const handleCreateMember = async (values) => {
    const isStatic = values?.membershipPriceType === "static";

    let getBody = {
      alwaysAvailable: values?.alwaysAvailable,
      description: values?.description,
      enableHouseHold: values?.enableHouseHold,
      formEndDate: values?.formEndDate,
      formName: values?.formName,
      formStartDate: values?.formStartDate,
      formTags: values?.formTags,
      membershipPriceType: values?.membershipPriceType,
      showLastYear: values?.showLastYear,
      validateContacts: values?.validateContacts,
      validateFamily: values?.validateFamily,
      showFinancialAid: values?.showFinancialAid,
      message: values?.message,
      ...(!isStatic && {
        ledgerForCalculation: {
          ledgers: values?.ledgersForCalculation,
          fallbackAmount: values?.ledgerForCalculation?.fallbackAmount,
          dateRange: {
            from: values?.ledgerForCalculation?.dateRange?.[0],
            to: values?.ledgerForCalculation?.dateRange?.[1],
          },
        },
      }),
      membershipPricing: values?.membershipPricing?.map((item) =>
        mapPricingItem(item, isStatic)
      ),
      addOns: values?.addOns?.map((item) => mapPricingItem(item, false, true)),
    };

    if (formId) {
      getBody = omitKeys(getBody, [
        "_id",
        "created_by",
        "__v",
        "updatedAt",
        "createdAt",
        "active",
        "organization",
        "archived",
      ]);
    }

    formId
      ? await updateMember(getBody, formId).then((res) => {
        toastr.success("Success", `Member has been updated successfully`);
        push("/forms?key=membership");
      })
      : await createMember(getBody).then((res) => {
        toastr.success("Success", `Member has been created successfully`);
        push("/forms?key=membership");
      });
  };

  return (
    <Box className="class__container create-event__container member_container">
      <FormikWizard
        innerRef={formikRef}
        initialValues={getInitialValues(memberById)}
        onSubmit={(values) => handleCreateMember(values)}
        enableReinitialize
        steps={[
          {
            component: FormInformation,
            validationSchema: informationSchema,
          },
          {
            component: () => (
              <Pricing ledgers={ledgers?.ledgers} loading={loading} />
            ),
            validationSchema: () => {
              const type = formikRef.current?.values?.membershipPriceType;
              return type === "static"
                ? membershipPricingSchema
                : dynamicPricingSchema;
            },
          },
          {
            component: () => (
              <CreateForm ledgers={ledgers?.ledgers} loading={loading} />
            ),
            validationSchema: finalFormSchema,
          },
        ]}
      >
        {({
          currentStepIndex,
          renderComponent,
          handlePrev,
          handleNext,
          handleSubmit,
          isSubmitting,
        }) => {
          const isLastStep = currentStepIndex === steps.length - 1;

          return (
            <form onSubmit={handleSubmit}>
              <Box className="top_section">
                <Box className="header__title">
                  {formId ? "Edit Form" : "New Form"}
                </Box>

                <Box className="class__steps">
                  <Stepper activeStep={currentStepIndex}>
                    {steps?.map((label, index) => (
                      <Step
                        key={label}
                        completed={currentStepIndex > index}
                        className="step"
                      >
                        <StepLabel className="step-label">{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>

                <Box className="action__section">
                  <Box className="action__buttons d-flex">
                    <MyButton
                      onClick={() => {
                        if (currentStepIndex === 0) {
                          push("/forms");
                        } else {
                          handlePrev();
                        }
                      }}
                      style={cancelButton}
                      type="button"
                      disabled={isSubmitting}
                    >
                      {currentStepIndex === 0 ? "Cancel" : "Previous"}
                    </MyButton>
                    <MyButton
                      onClick={() => {
                        if (isLastStep) {
                          handleSubmit();
                        } else {
                          touchStepFields(formikRef, currentStepIndex);
                          handleNext();
                        }
                      }}
                      style={submitButton}
                      type="button"
                      disabled={isSubmitting}
                    >
                      {isLastStep ? <span>Save</span> : "Continue"}
                    </MyButton>
                  </Box>
                </Box>
              </Box>

              <Box>{renderComponent()}</Box>
            </form>
          );
        }}
      </FormikWizard>
    </Box>
  );
};

export default CreateMember;
